import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsTresorerie, usePlanTresorerie } from '@/api/hooks';
import type { BlocPlan } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { Drapeaux, EtatPlanBadge, OngletsTresorerie, SEMAINE_ISO, decalerSemaine, estDirecteurFinancier, estResponsableTresorerie, libelleSemaine, semaineCourante } from '@/components/tresorerie';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateHeureFr, montant } from '@/lib/utils';

function Bloc({ b }: { b: BlocPlan }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {b.compte ? `${b.compte.banque} · ${b.compte.code}` : 'Compte non encore choisi'}
          {b.depassement && <Badge variant="destructive">Dépassement du disponible</Badge>}
        </CardTitle>
        <CardDescription>
          Programmé {montant(b.programme, true)} · payé {montant(b.paye, true)}
          {b.soldeDisponible !== null && ` · disponible ${montant(b.soldeDisponible, true)}`}
          {b.soldePrevisionnel !== null && ` · prévisionnel ${montant(b.soldePrevisionnel, true)}`}
          {b.couverture !== null && ` · couverture ${b.couverture} %`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {b.lignes.length === 0 ? (
          <Vide message="Aucun paiement sur ce compte cette semaine." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>FED</TableHead>
                <TableHead>Objet · bénéficiaire</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="hidden md:table-cell">Chargé du paiement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {b.lignes.map((l) => (
                <TableRow key={l.id} className={cn(l.etat === 'REPORTE' && 'opacity-60')}>
                  <TableCell>
                    <Link to={`/tresorerie/dossiers/${l.id}`} className="font-medium text-primary hover:underline">
                      {l.fed.numero ?? 'FED'}
                    </Link>
                    <div className="text-xs text-muted-foreground">{l.fed.structureCode}</div>
                  </TableCell>
                  <TableCell className="max-w-[34ch]">
                    <div className="truncate">{l.fed.objet}</div>
                    <div className="truncate text-xs text-muted-foreground">{l.fed.beneficiaire ?? l.fed.emetteur?.nom}</div>
                    <Drapeaux d={l} />
                    {l.report && (
                      <div className="text-xs text-amber-800">
                        reporté vers {l.report.vers}
                        {l.report.parDf ? ' par le DF' : ''} : {l.report.motif}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="num text-right whitespace-nowrap">{montant(l.fed.montantUsd, true)}</TableCell>
                  <TableCell>
                    <EtatPlanBadge etat={l.etat} />
                    <div className="text-xs text-muted-foreground">{l.libelleStatut}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{l.tresorier?.nom ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** Plan de décaissement hebdomadaire (RT-4, § 9) : enveloppe, charge, blocs par compte, arrêt et visa du DF. */
export function PlanDecaissement() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const [params, setParams] = useSearchParams();
  const demandee = params.get('semaine') ?? '';
  const semaine = SEMAINE_ISO.test(demandee) ? demandee : semaineCourante();
  const aller = (s: string) => setParams({ semaine: s }, { replace: true });
  const { data, isLoading, error } = usePlanTresorerie(semaine);
  const actions = useActionsTresorerie();
  const [enveloppe, setEnveloppe] = useState<string | null>(null);
  const responsable = estResponsableTresorerie(profils);
  const df = estDirecteurFinancier(profils);
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setEnveloppe(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };

  return (
    <div>
      <PageHeader
        titre="Plan de décaissement"
        sousTitre={libelleSemaine(semaine)}
        actions={
          <>
            <Button variant="outline" size="icon" onClick={() => aller(decalerSemaine(semaine, -1))} aria-label="Semaine précédente">
              <ChevronLeft className="size-4" />
            </Button>
            <Button variant="outline" onClick={() => aller(semaineCourante())}>
              Cette semaine
            </Button>
            <Button variant="outline" size="icon" onClick={() => aller(decalerSemaine(semaine, 1))} aria-label="Semaine suivante">
              <ChevronRight className="size-4" />
            </Button>
            <BoutonExcel chemin={`/tresorerie/plan.xlsx?semaine=${semaine}`} nom={`tresorerie-plan-${semaine}.xlsx`} taille="default" />
            {responsable && data && (
              <Button variant="outline" onClick={() => setEnveloppe(data.enveloppe === null ? '' : String(data.enveloppe))}>
                Enveloppe
              </Button>
            )}
            {responsable && data && !data.arrete && <Button onClick={() => executer(() => actions.arreterPlan.mutateAsync(semaine), 'Plan arrêté ; le Directeur financier est notifié.')}>Arrêter le plan</Button>}
            {df && data?.arrete && !data.viseDf && <Button onClick={() => executer(() => actions.viserPlan.mutateAsync(semaine), 'Plan visé.')}>Viser le plan</Button>}
          </>
        }
      />
      <OngletsTresorerie />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <div className="space-y-6">
          {data.alerteCharge && (
            <Alert variant="destructive">
              <AlertTitle>Charge du plan à {data.charge} % de l'enveloppe</AlertTitle>
              <AlertDescription>Au-delà de {data.seuilCharge} %, le Directeur financier est alerté et peut reporter des FED à la semaine suivante.</AlertDescription>
            </Alert>
          )}
          {data.modificationsApresArret.length > 0 && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTitle>Modifications après l'arrêt du plan</AlertTitle>
              <AlertDescription>
                {data.modificationsApresArret.map((m, i) => (
                  <div key={i}>
                    {dateHeureFr(m.date)} · {m.action} · FED {m.numero ?? ''} · {m.auteurNom}
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <Kpi label="Programmé" valeur={montant(data.totaux.programme)} detail={`${montant(data.totaux.inscrits)} inscrits · ${montant(data.totaux.proposes)} proposés`} />
            <Kpi label="Enveloppe" valeur={data.enveloppe === null ? '—' : montant(data.enveloppe)} detail={data.charge === null ? 'non fixée' : `charge ${data.charge} %`} accent={data.alerteCharge ? 'critique' : undefined} />
            <Kpi label="Payés" valeur={montant(data.totaux.payes)} accent="ok" />
            <Kpi label="Reportés" valeur={montant(data.totaux.reportes)} accent={data.totaux.reportes > 0 ? 'alerte' : undefined} />
            <Kpi label="État du plan" valeur={data.viseDf ? 'Visé DF' : data.arrete ? 'Arrêté' : 'Ouvert'} detail={data.viseDf ? dateHeureFr(data.dateVisa) : data.arrete ? dateHeureFr(data.dateArret) : 'ajouts et reports libres'} accent={data.viseDf ? 'ok' : undefined} />
          </div>
          {data.totaux.suspendus > 0 && <div className="text-sm text-red-700">{montant(data.totaux.suspendus)} suspendus par une note explicative.</div>}
          {data.blocs.length === 0 ? <Vide message="Aucun FED programmé cette semaine." /> : data.blocs.map((b, i) => <Bloc key={b.compte?.id ?? `sans-${i}`} b={b} />)}
        </div>
      )}
      <Dialog open={enveloppe !== null} onOpenChange={(o) => !o && setEnveloppe(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enveloppe de décaissement · {semaine}</DialogTitle>
            <DialogDescription>Trésorerie prévisionnelle mobilisable pour les paiements de la semaine ; la charge du plan s'y compare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="enveloppe">Montant (USD)</Label>
            <Input id="enveloppe" type="number" min={0} step="0.01" value={enveloppe ?? ''} onChange={(e) => setEnveloppe(e.target.value)} autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnveloppe(null)}>
              Fermer
            </Button>
            <Button onClick={() => executer(() => actions.enveloppe.mutateAsync({ semaine, enveloppe: Number(enveloppe) }), 'Enveloppe enregistrée.')} disabled={actions.enveloppe.isPending || enveloppe === '' || enveloppe === null}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
